import React, { useEffect, useState } from 'react';
import {
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from 'components/icons';
import { pipelineApi } from '../../services/api';

interface EligibilityIssue {
  code: string;
  severity: 'fatal' | 'warning';
  category: 'eligibility' | 'deadline' | 'profile' | 'geographic' | 'performance';
  title: string;
  detail: string;
  actionHint?: string;
}

interface EligibilityReport {
  fatalCount: number;
  warningCount: number;
  canBid: boolean;
  issues: EligibilityIssue[];
}

interface Props {
  opportunityId: string;
}

export const EligibilityGate: React.FC<Props> = ({ opportunityId }) => {
  const [report, setReport] = useState<EligibilityReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    pipelineApi
      .getEligibility(opportunityId)
      .then(res => {
        if (cancelled) return;
        setReport(res.data?.data || null);
        const data = res.data?.data;
        // Auto-expand on anything fatal — the user must see those.
        //
        // Also on the FAR 52.219-14 subcontracting limit, which is only a
        // warning but is the one warning that changes what gets built rather
        // than what gets known: it decides the teaming split, and the split has
        // to be settled before anything is priced. Collapsed behind "1 warning"
        // it would be found after the bid was already shaped.
        const structural = (data?.issues ?? []).some(
          (i: EligibilityIssue) => i.code === 'SUBCONTRACTING_LIMIT',
        );
        if (data?.fatalCount > 0 || structural) setExpanded(true);
      })
      .catch(() => {
        if (!cancelled) setReport(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [opportunityId]);

  if (loading || !report) return null;

  // Clean state — show a small green pill, not a banner
  if (report.fatalCount === 0 && report.warningCount === 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">
        <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
        <span>No eligibility issues detected. You can bid on this opportunity.</span>
      </div>
    );
  }

  const tone = report.fatalCount > 0 ? 'fatal' : 'warning';
  const styles =
    tone === 'fatal'
      ? {
          container: 'bg-red-50 border-red-300 text-red-900',
          header: 'text-red-900',
          icon: 'text-red-600',
        }
      : {
          container: 'bg-amber-50 border-amber-300 text-amber-900',
          header: 'text-amber-900',
          icon: 'text-amber-600',
        };

  const Icon = tone === 'fatal' ? ShieldExclamationIcon : ExclamationTriangleIcon;

  return (
    <div className={`rounded-lg border p-3 sm:p-4 ${styles.container}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div className="flex items-start gap-2 min-w-0">
          <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${styles.icon}`} />
          <div className="min-w-0">
            <p className={`text-sm font-semibold ${styles.header}`}>
              {tone === 'fatal'
                ? `${report.fatalCount} eligibility issue${report.fatalCount !== 1 ? 's' : ''} detected`
                : `${report.warningCount} warning${report.warningCount !== 1 ? 's' : ''}`}
              {tone === 'fatal' && report.warningCount > 0 && (
                <span className="font-normal opacity-75">
                  {' '}+ {report.warningCount} warning{report.warningCount !== 1 ? 's' : ''}
                </span>
              )}
            </p>
            <p className="text-xs opacity-75 mt-0.5">
              {tone === 'fatal'
                ? 'Review before spending time on a proposal'
                : report.issues.some(i => i.code === 'SUBCONTRACTING_LIMIT')
                ? 'One of these affects how much of the work you can subcontract'
                : 'Review before submitting'}
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUpIcon className="h-4 w-4 flex-shrink-0" />
        ) : (
          <ChevronDownIcon className="h-4 w-4 flex-shrink-0" />
        )}
      </button>

      {expanded && (
        <ul className="mt-3 space-y-2">
          {report.issues.map(issue => (
            <li
              key={issue.code}
              className={`text-sm rounded-md p-2.5 border ${
                issue.severity === 'fatal'
                  ? 'bg-surface border-red-200'
                  : 'bg-surface border-amber-200'
              }`}
            >
              <div className="flex items-start gap-2">
                <span
                  className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                    issue.severity === 'fatal'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}
                >
                  {issue.severity}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-content">{issue.title}</p>
                  <p className="text-xs text-content-muted mt-0.5">{issue.detail}</p>
                  {issue.actionHint && (
                    <p className="text-xs text-content-muted mt-1 italic">
                      → {issue.actionHint}
                    </p>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
